/**
 * ============================================================
 * assets/wishlist.js
 * BEAT SPACE - お気に入り（Wishlist）コアロジック
 *
 * 役割:
 *  - LocalStorage への商品ID保存／削除／取得
 *  - ページ上の [data-wishlist-button] の状態同期とクリック処理
 *  - 変更時に `wishlist:updated` カスタムイベントを発火
 *    （ヘッダーのカウンター表示やマイページ側が購読できる）
 *
 * データ構造（LocalStorage）:
 *  key:   "beatspace:wishlist:v1"
 *  value: JSON配列
 *    [
 *      { "id": 1234567890, "handle": "dark-trap-beat", "addedAt": "2026-07-07T09:00:00.000Z" },
 *      ...
 *    ]
 *
 *  ※ ステップ2（マイページ）で /products/{handle}.js から商品情報を
 *    取得して一覧描画するため、id だけでなく handle も保存しておく。
 * ============================================================
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'beatspace:wishlist:v1';
  var MAX_ITEMS = 200; // 念のための上限（LocalStorage肥大化防止）

  /* ----------------------------------------------------------
   * ストレージ層（読み書きは必ずこの2関数を経由する）
   * -------------------------------------------------------- */

  function readItems() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // 不正なエントリを除外
      return parsed.filter(function (item) {
        return item && typeof item === 'object' && item.id != null;
      });
    } catch (e) {
      // JSON破損・LocalStorage無効（プライベートブラウズ等）
      console.warn('[BeatSpaceWishlist] 読み込みに失敗しました:', e);
      return [];
    }
  }

  function writeItems(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      // 容量超過や無効化されている場合
      console.warn('[BeatSpaceWishlist] 保存に失敗しました:', e);
      return false;
    }
  }

  /* ----------------------------------------------------------
   * 公開API
   * -------------------------------------------------------- */

  var Wishlist = {
    /** 保存中の全アイテムを取得 */
    getItems: function () {
      return readItems();
    },

    /** 保存中の商品ID一覧（文字列化して返す） */
    getIds: function () {
      return readItems().map(function (item) {
        return String(item.id);
      });
    },

    /** 件数 */
    getCount: function () {
      return readItems().length;
    },

    /** 指定IDが登録済みか */
    has: function (productId) {
      var id = String(productId);
      return readItems().some(function (item) {
        return String(item.id) === id;
      });
    },

    /** 追加（重複は無視）。成功時 true */
    add: function (productId, handle) {
      var items = readItems();
      var id = String(productId);

      var exists = items.some(function (item) {
        return String(item.id) === id;
      });
      if (exists) return true;

      if (items.length >= MAX_ITEMS) {
        items.shift(); // 最も古いものを削除
      }

      items.push({
        id: id,
        handle: handle || '',
        addedAt: new Date().toISOString()
      });

      var ok = writeItems(items);
      if (ok) emitUpdated(items, { action: 'add', id: id });
      return ok;
    },

    /** 削除。成功時 true */
    remove: function (productId) {
      var id = String(productId);
      var items = readItems().filter(function (item) {
        return String(item.id) !== id;
      });
      var ok = writeItems(items);
      if (ok) emitUpdated(items, { action: 'remove', id: id });
      return ok;
    },

    /** トグル。処理後に「登録済みかどうか」を返す */
    toggle: function (productId, handle) {
      if (this.has(productId)) {
        this.remove(productId);
        return false;
      }
      this.add(productId, handle);
      return true;
    },

    /** 全削除 */
    clear: function () {
      writeItems([]);
      emitUpdated([], { action: 'clear' });
    }
  };

  // マイページ（ステップ2）や他スクリプトから使えるように公開
  window.BeatSpaceWishlist = Wishlist;

  /* ----------------------------------------------------------
   * イベント発火
   * -------------------------------------------------------- */

  function emitUpdated(items, detailExtra) {
    var detail = Object.assign(
      { items: items, count: items.length },
      detailExtra || {}
    );
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: detail }));
  }

  /* ----------------------------------------------------------
   * ボタンUIとの連携
   * -------------------------------------------------------- */

  /** 1つのボタンの見た目・属性を現在の状態に合わせる */
  function syncButton(button) {
    var id = button.getAttribute('data-product-id');
    var title = button.getAttribute('data-product-title') || '';
    var labelAdd = button.getAttribute('data-label-add') || 'お気に入りに追加';
    var labelRemove = button.getAttribute('data-label-remove') || 'お気に入りから削除';
    var active = Wishlist.has(id);

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      title + ' を' + (active ? labelRemove : labelAdd)
    );

    var text = button.querySelector('[data-wishlist-button-text]');
    if (text) {
      text.textContent = active ? labelRemove : labelAdd;
    }
  }

  /** ページ内の全ボタンを同期（初期表示・タブ間同期・動的追加後に使用） */
  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach(syncButton);
  }

  // 動的に追加された要素（Ajaxで読み込まれた商品カード等）にも効くよう、
  // クリックは document へのイベント委譲で受ける
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-wishlist-button]');
    if (!button) return;

    event.preventDefault();

    var id = button.getAttribute('data-product-id');
    var handle = button.getAttribute('data-product-handle');
    if (!id) return;

    var nowActive = Wishlist.toggle(id, handle);

    // 押した瞬間のマイクロアニメーション用クラス
    if (nowActive) {
      button.classList.add('is-animating');
      button.addEventListener(
        'animationend',
        function () {
          button.classList.remove('is-animating');
        },
        { once: true }
      );
    }
  });

  // 自分のタブでの変更 → 同一商品IDの全ボタン（一覧カード＋商品ページ等）を同期
  document.addEventListener('wishlist:updated', syncAllButtons);

  // 別タブでの変更（storageイベントは他タブでのみ発火する）
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) {
      syncAllButtons();
      emitUpdated(readItems(), { action: 'external-sync' });
    }
  });

  // 初期同期
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllButtons);
  } else {
    syncAllButtons();
  }
})();